import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';

const BlogAIGenerator = ({ onGenerationComplete, onCancel, token }) => {
  const [formData, setFormData] = useState({
    topic: '',
    audience: 'General',
    tone: 'Professional',
    length: '800-1200',
    key_points: '',
    keywords: '',
    author: ''
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setGenerating(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/blog/ai/generate`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      onGenerationComplete(response.data);
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate blog post. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-lg p-8 max-w-3xl mx-auto"
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI Blog Post Creator</h2>
            <p className="text-sm text-gray-600">Generate a complete blog post in seconds</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-red-900 mb-1">Generation Failed</h4>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Topic */}
        <div>
          <Label htmlFor="topic" className="text-sm font-semibold text-gray-700 mb-2 block">
            What's this article about? <span className="text-red-500">*</span>
          </Label>
          <textarea
            id="topic"
            required
            value={formData.topic}
            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            placeholder="e.g., Announce our SAP Partner Award win for customer satisfaction"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            disabled={generating}
          />
          <p className="text-xs text-gray-500 mt-1">Describe the topic, purpose, or key message of your blog post</p>
        </div>

        {/* Audience & Tone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="audience" className="text-sm font-semibold text-gray-700 mb-2 block">
              Target Audience
            </Label>
            <Select
              value={formData.audience}
              onValueChange={(value) => setFormData({ ...formData, audience: value })}
              disabled={generating}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="General">General Audience</SelectItem>
                <SelectItem value="SME Decision Makers">SME Decision Makers</SelectItem>
                <SelectItem value="IT Directors">IT Directors</SelectItem>
                <SelectItem value="Finance Directors">Finance Directors</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="tone" className="text-sm font-semibold text-gray-700 mb-2 block">
              Writing Tone
            </Label>
            <Select
              value={formData.tone}
              onValueChange={(value) => setFormData({ ...formData, tone: value })}
              disabled={generating}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Professional">Professional & Formal</SelectItem>
                <SelectItem value="Conversational">Conversational & Friendly</SelectItem>
                <SelectItem value="Technical">Technical & Detailed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Length */}
        <div>
          <Label htmlFor="length" className="text-sm font-semibold text-gray-700 mb-2 block">
            Target Length
          </Label>
          <Select
            value={formData.length}
            onValueChange={(value) => setFormData({ ...formData, length: value })}
            disabled={generating}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="700-900">Short (700-900 words) - Quick reads, announcements</SelectItem>
              <SelectItem value="800-1200">Medium (800-1,200 words) - Standard articles ⭐</SelectItem>
              <SelectItem value="1200-1500">Long (1,200-1,500 words) - In-depth guides</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Key Points */}
        <div>
          <Label htmlFor="key_points" className="text-sm font-semibold text-gray-700 mb-2 block">
            Key Points to Include (Optional)
          </Label>
          <textarea
            id="key_points"
            value={formData.key_points}
            onChange={(e) => setFormData({ ...formData, key_points: e.target.value })}
            placeholder="• Point 1&#10;• Point 2&#10;• Point 3"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            rows={4}
            disabled={generating}
          />
        </div>

        {/* Keywords */}
        <div>
          <Label htmlFor="keywords" className="text-sm font-semibold text-gray-700 mb-2 block">
            Primary Keywords (Optional)
          </Label>
          <input
            id="keywords"
            type="text"
            value={formData.keywords}
            onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
            placeholder="e.g., SAP Business One, ERP benefits, SME"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={generating}
          />
          <p className="text-xs text-gray-500 mt-1">Separate with commas. AI will suggest keywords if left empty.</p>
        </div>

        {/* Author Name */}
        <div>
          <Label htmlFor="author" className="text-sm font-semibold text-gray-700 mb-2 block">
            Author Name (Optional)
          </Label>
          <input
            id="author"
            type="text"
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            placeholder="e.g., John Smith, Sarah Jones"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={generating}
          />
          <p className="text-xs text-gray-500 mt-1">Leave empty to use your account name as author.</p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-4 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={generating}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={generating || !formData.topic.trim()}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Amazing Content...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Complete Article
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>⚡ What you'll get:</strong> Complete blog post with H1, H2 sections, meta description, SEO tags, internal links, and FAQs - ready for review in ~15 seconds!
        </p>
      </div>
    </motion.div>
  );
};

export default BlogAIGenerator;